import { useId, useState } from 'react';

import {
  Box,
  ButtonBase,
  Collapse,
  Paper,
  Stack,
  Typography,
} from '@mui/material';

import KeyboardArrowDownRoundedIcon
  from '@mui/icons-material/KeyboardArrowDownRounded';
import KeyboardArrowRightRoundedIcon
  from '@mui/icons-material/KeyboardArrowRightRounded';

import CustomerRelationshipEconomicReadingDetail
  from './CustomerRelationshipEconomicReadingDetail';

import {
  buildCustomerProductEconomicReadings,
} from '../utils/customerProductEconomicReadings';

import type {
  MarginCenterCustomerProductRecentTransaction,
  MarginCenterCustomerProductSelection,
  MarginCenterCustomerProductSeriesPoint,
  MarginCenterCustomerRelationshipChanges,
  MarginCenterCustomerRelationshipCoverage,
  MarginCenterCustomerRelationshipPeriodMetrics,
} from '../types/marginCenter';

import type {
  MarginCenterEconomicMetricMovement,
  MarginCenterEconomicReadingKey,
} from '../utils/customerProductEconomicReadings';


const numberFormatter = new Intl.NumberFormat(
  'es-ES',
  {
    maximumFractionDigits: 2,
  },
);


function formatMovement(
  movement: MarginCenterEconomicMetricMovement,
): string {
  if (
    movement.value === null
    || !Number.isFinite(movement.value)
  ) {
    return '—';
  }

  const sign =
    movement.value > 0
      ? '+'
      : '';

  const formattedValue =
    numberFormatter.format(
      movement.value,
    );

  if (
    movement.unit
    === 'percentage_points'
  ) {
    return `${sign}${formattedValue} p.p.`;
  }

  return `${sign}${formattedValue} %`;
}


function EconomicMovement({
  movement,
}: {
  movement: MarginCenterEconomicMetricMovement;
}) {
  const color = movement.direction === 'up'
    ? '#34d399'
    : movement.direction === 'down'
      ? '#fb7185'
      : '#94a3b8';

  return (
    <Box
      sx={{
        minWidth: 0,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          color: '#64748b',
          fontWeight: 700,
        }}
      >
        {movement.label}
      </Typography>

      <Typography
        variant="body2"
        sx={{
          mt: 0.15,
          color,
          fontWeight: 850,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {formatMovement(movement)}
      </Typography>
    </Box>
  );
}


export default function CustomerRelationshipEconomicReadings({
  changes,
  coverage,
  current,
  comparison,
  selection,
  series,
  recentTransactions,
  currencyCode,
}: {
  changes:
    | MarginCenterCustomerRelationshipChanges
    | null;

  coverage:
    | MarginCenterCustomerRelationshipCoverage
    | null;

  current: MarginCenterCustomerRelationshipPeriodMetrics;
  comparison: MarginCenterCustomerRelationshipPeriodMetrics | null;
  selection: MarginCenterCustomerProductSelection;
  series: MarginCenterCustomerProductSeriesPoint[];
  recentTransactions: MarginCenterCustomerProductRecentTransaction[];
  currencyCode: string;
}) {
  const [selectedKey, setSelectedKey] =
    useState<MarginCenterEconomicReadingKey | null>(null);
  const detailId = useId();

  const readings =
    buildCustomerProductEconomicReadings({
      changes,
      coverage,
    });

  const coverageNote =
    readings.find(
      (reading) =>
        Boolean(reading.contextNote),
    )?.contextNote ?? null;

  const evaluableReadings =
    readings.filter(
      (reading) => reading.evaluable,
    );

  const movementReadings =
    evaluableReadings.filter(
      (reading) => reading.hasMovement,
    );

  const hasEvaluableReadings =
    evaluableReadings.length > 0;

  const hasMovementReadings =
    movementReadings.length > 0;

  const selectedReading = movementReadings.find(
    (reading) => reading.key === selectedKey,
  ) ?? null;

  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.5,
        borderRadius: 2.5,
        border:
          '1px solid rgba(148,163,184,0.14)',
        background:
          'linear-gradient(180deg, rgba(15,23,42,0.58) 0%, rgba(8,17,31,0.78) 100%)',
        boxShadow:
          '0 10px 28px rgba(2,6,23,0.14)',
      }}
    >
      <Stack
        direction={{
          xs: 'column',
          md: 'row',
        }}
        spacing={1}
        justifyContent="space-between"
        alignItems={{
          xs: 'flex-start',
          md: 'baseline',
        }}
      >
        <Box>
          <Typography
            variant="subtitle2"
            sx={{
              color: '#f8fafc',
              fontWeight: 850,
            }}
          >
            Lectura económica combinada
          </Typography>

          <Typography
            variant="caption"
            sx={{
              display: 'block',
              mt: 0.2,
              color: '#64748b',
            }}
          >
            Cruza movimientos observados frente al
            periodo comparado. Describe simultaneidad,
            no causalidad.
          </Typography>
        </Box>

        {(
          !changes
          || !hasEvaluableReadings
          || !hasMovementReadings
        ) && (
          <Typography
            variant="caption"
            sx={{
              color: '#64748b',
              fontWeight: 700,
            }}
          >
            {!changes
              ? 'Sin periodo comparado'
              : !hasEvaluableReadings
                ? 'Sin base comparable'
                : 'Sin cambios combinados'}
          </Typography>
        )}
      </Stack>

      {changes && hasMovementReadings ? (
        <>
          <Box
            sx={{
              mt: 1.25,
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                md: 'repeat(3, minmax(0, 1fr))',
              },
              gap: 1,
            }}
          >
            {movementReadings.map(
              (reading) => {
                const selected = selectedReading?.key === reading.key;
                return (
                  <ButtonBase
                    key={reading.key}
                    onClick={() => setSelectedKey(
                      selected ? null : reading.key,
                    )}
                    aria-expanded={selected}
                    aria-controls={detailId}
                    sx={{
                      display: 'block',
                      width: '100%',
                      minWidth: 0,
                      p: 1.25,
                      textAlign: 'left',
                      borderRadius: 1.75,
                      border: selected
                        ? '1px solid rgba(56,189,248,0.62)'
                        : '1px solid rgba(148,163,184,0.18)',
                      bgcolor: selected
                        ? 'rgba(14,165,233,0.09)'
                        : 'rgba(15,23,42,0.32)',
                      transition: 'background-color 140ms ease, border-color 140ms ease',
                      '&:hover': {
                        bgcolor: 'rgba(56,189,248,0.08)',
                        borderColor: 'rgba(56,189,248,0.43)',
                      },
                      '&:focus-visible': {
                        outline: '2px solid rgba(56,189,248,0.78)',
                        outlineOffset: 2,
                      },
                    }}
                  >
                    <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
                      <Typography
                        variant="overline"
                        sx={{ color: '#94a3b8', fontWeight: 850, letterSpacing: 0.7, lineHeight: 1.1 }}
                      >
                        {reading.title}
                      </Typography>
                      {selected
                        ? <KeyboardArrowDownRoundedIcon sx={{ color: '#38bdf8', fontSize: 19 }} />
                        : <KeyboardArrowRightRoundedIcon sx={{ color: '#94a3b8', fontSize: 19 }} />}
                    </Stack>
                    <Typography
                      variant="body2"
                      sx={{
                        mt: 0.6,
                        minHeight: { xs: 'auto', md: 40 },
                        color: '#e2e8f0',
                        fontWeight: 700,
                        lineHeight: 1.4,
                      }}
                    >
                      {reading.summary}
                    </Typography>
                    <Box
                      sx={{
                        mt: 1,
                        pt: 1,
                        borderTop: '1px solid rgba(148,163,184,0.10)',
                        display: 'grid',
                        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                        gap: 1,
                      }}
                    >
                      <EconomicMovement movement={reading.primary} />
                      <EconomicMovement movement={reading.secondary} />
                    </Box>
                    <Typography
                      variant="caption"
                      sx={{ display: 'block', mt: 1, color: selected ? '#7dd3fc' : '#64748b', fontWeight: 750 }}
                    >
                      {selected ? 'Ocultar investigación' : 'Investigar comportamiento'}
                    </Typography>
                  </ButtonBase>
                );
              },
            )}
          </Box>

          <Collapse in={selectedReading !== null} unmountOnExit>
            <Box id={detailId} sx={{ mt: 1.25 }}>
              {selectedReading && (
                <CustomerRelationshipEconomicReadingDetail
                  key={selectedReading.key}
                  reading={selectedReading}
                  current={current}
                  comparison={comparison}
                  selection={selection}
                  coverage={coverage}
                  series={series}
                  recentTransactions={recentTransactions}
                  currencyCode={currencyCode}
                  onClose={() => setSelectedKey(null)}
                />
              )}
            </Box>
          </Collapse>

          {coverageNote && (
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                mt: 1,
                color: '#64748b',
              }}
            >
              {coverageNote}
            </Typography>
          )}
        </>
      ) : changes && hasEvaluableReadings ? (
        <Box
          sx={{
            mt: 1.1,
            px: 1.25,
            py: 1,
            borderRadius: 1.5,
            border:
              '1px solid rgba(148,163,184,0.10)',
            bgcolor:
              'rgba(15,23,42,0.24)',
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: '#94a3b8',
              fontWeight: 650,
            }}
          >
            No se observan cambios simultáneos en las
            combinaciones económicas evaluables frente
            al periodo comparado.
          </Typography>

          {coverageNote && (
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                mt: 0.45,
                color: '#64748b',
              }}
            >
              {coverageNote}
            </Typography>
          )}
        </Box>
      ) : changes ? (
        <Box
          sx={{
            mt: 1.1,
            px: 1.25,
            py: 1,
            borderRadius: 1.5,
            border:
              '1px solid rgba(148,163,184,0.10)',
            bgcolor:
              'rgba(15,23,42,0.24)',
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: '#94a3b8',
              fontWeight: 650,
            }}
          >
            No hay base comparable suficiente para
            cruzar ingresos, margen, precio, cantidad
            y coste en este periodo.
          </Typography>

          {coverageNote && (
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                mt: 0.45,
                color: '#64748b',
              }}
            >
              {coverageNote}
            </Typography>
          )}
        </Box>
      ) : (
        <Typography
          variant="body2"
          sx={{
            mt: 1.1,
            color: '#94a3b8',
          }}
        >
          Activa una comparación para observar
          conjuntamente ingresos, margen, precio,
          cantidad y coste.
        </Typography>
      )}
    </Paper>
  );
}